/**
 * /wireline-slickline/tool-strings — the tenant's tool-string templates
 * (GET /wireline-slickline/tool-strings, fieldiq-api#1336, WL-1).
 *
 * A tool string is an ordered list of components (rope socket, stem, jars,
 * …) a crew runs on a line. Full-width register; a row opens the string on
 * its own page, where its items are edited.
 *
 * The total length and widest OD are the API's, shown as sent: the length as
 * `totalLength` in the tenant's `lengthUnit` (api#1339, converted there, never
 * here), the OD as `maxOdMm`. The API leaves the total null unless every item
 * has a length, and that null shows as `—`.
 *
 * New tool string is WL_GEAR_WRITE (admin, office_staff, supervisor).
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Wrench } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useWlToolStrings } from '../../hooks/useWirelineSlickline';
import { queryLoadFailure } from '../../lib/load-failure';
import { canWriteWlGear, wlLineLength, wlWithUnit } from '../../lib/wireline-slickline';
import type { WlToolString } from '../../types/wireline-slickline';
import { useOpenRecord } from '../downhole/recordPage';
import { WL_EYEBROW, WL_RAIL, WlEmpty, WlPanel, WlRegister, type WlColumn } from './WlBits';

export default function WlToolStringsScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const query = useWlToolStrings();
  const strings = query.data?.data ?? [];
  const loading = query.isLoading;
  const known = loading || queryLoadFailure(query) ? undefined : strings;
  const canAdd = canWriteWlGear(user?.role);

  const columns: WlColumn<WlToolString>[] = [
    { key: 'name', header: 'Tool string', render: (t) => <p className="font-extrabold text-white">{t.name || '—'}</p> },
    { key: 'type', header: 'Line type', render: (t) => (t.lineType ? t.lineTypeLabel ?? t.lineType : 'Any') },
    { key: 'items', header: 'Items', align: 'right', render: (t) => <span className="font-mono text-sm">{displayValue(t.itemCount)}</span> },
    { key: 'length', header: 'Length', align: 'right', render: (t) => <span className="font-mono text-sm">{wlLineLength(t.totalLength, t.lengthUnit)}</span> },
    { key: 'od', header: 'Max OD', align: 'right', render: (t) => <span className="font-mono text-sm">{wlWithUnit(t.maxOdMm, 'mm')}</span> },
  ];

  return (
    <div data-testid="wl-tool-strings-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WL_EYEBROW}
        title="Tool strings"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canAdd ? (
            <button type="button" onClick={() => openRecord('/wireline-slickline/tool-strings/new')} className="fi-action fi-action--standard fi-action--primary">
              <Plus className="size-4" aria-hidden="true" />
              New tool string
            </button>
          ) : undefined
        }
        stats={[{ icon: <Wrench className="size-4" aria-hidden="true" />, label: 'Tool strings', sub: 'Templates', tone: 'neutral', loading, value: displayValue(known?.length) }]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <WlPanel
            eyebrow="Tool-string templates"
            title="Tool strings"
            testId="wl-tool-string-register"
            footer={!loading && known && strings.length > 0 ? `${strings.length} tool string${strings.length === 1 ? '' : 's'}` : undefined}
          >
            <WlRegister
              query={query}
              rows={strings}
              columns={columns}
              rail={() => WL_RAIL}
              onOpen={(t) => openRecord(`/wireline-slickline/tool-strings/${t.id}`)}
              subject="tool strings"
              testId="wl-tool-string"
              minWidth="min-w-[700px]"
              empty={<WlEmpty>There are no tool strings yet.</WlEmpty>}
              card={(t) => (
                <>
                  <p className="text-sm font-extrabold text-white">{t.name || '—'}</p>
                  <p className="mt-0.5 text-xs text-fieldiq-muted">
                    {t.lineType ? t.lineTypeLabel ?? t.lineType : 'Any line'} · {displayValue(t.itemCount)} item{t.itemCount === 1 ? '' : 's'} · {wlLineLength(t.totalLength, t.lengthUnit)}
                  </p>
                </>
              )}
            />
          </WlPanel>
        </div>
      </section>
    </div>
  );
}
